import { Button, Card, Grid, TextField, Typography } from '@mui/material';
import { Box, Container } from '@mui/system';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import FormHeaderGreeting from 'src/components/Greeting/FormHeaderGreeting';
import { RootState } from 'src/redux-store/appStore';
import FormikForm from 'src/ui-component/FormikFormLibrary';
import { Fields } from 'src/ui-component/FormikFormLibrary/FormikTypes';
import Breadcrumb from 'src/ui-component/Breadcrumb';
import LoadingOverlay from 'src/components/common/LoadingOverlay';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';
import { handleError } from 'src/redux-store/slices/errorSlice';
import PdfGenerator from 'src/ui-component/PdfGenerator';
import CustomModal from 'src/ui-component/customModal/CustomModal';
import FormikSelect from 'src/ui-component/FormikFormLibrary/FormikSelect';
import CloseIcon from '@mui/icons-material/Close';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import { sendNotification } from 'src/utils/sendNotification';
import CustomButtonContained from 'src/components/CustomButton/CustomButtonContained';
import EditFormfeatureViews from 'src/components/EditFormfeatureViews/EditFormfeatureViews';
import { getApplicationDetails } from '../common/getApplicationDetails';
import { DraftCheck } from 'src/types/PZPermit/pzPermit.type';
import UploadDocs from 'src/ui-component/UploadDocs';
import { shouldShowEdit } from 'src/utils/CommonFunctions';

const EditApplication: React.FC = () => {
  const navigate = useNavigate();
  const messageBodyRef = useRef<string>('');
  const userData = useSelector((store: RootState) => store.user.userDetails);
  const dispatch = useDispatch();
  const location = useLocation();
  const { townshipId, permit, applicationID } = useParams();
  const userDetails = useSelector((store: RootState) => store.user.userDetails);
  const currentPermitRole = useSelector(
    (state: RootState) =>
      state?.currentPermit?.currentPermitDetails?.currentPermitUserRole
  );
  const socket = useSelector((store: RootState) => store.socket.socket);
  const isManager = currentPermitRole === 'Manager';
  const [openApprovePopup, setOpenApprovePopup] = useState<boolean>(false);
  const [switchTo, setSwitchTo] = useState('');
  const [documentOptions, setDocumentOptions] = useState<string[]>([
    'Select Type'
  ]);
  const [IsOpen, setIsOpen] = useState<boolean>(false);
  const [docsErrorMessege, setDocsErrorMessege] = useState<string>('');
  const [file, setFile] = useState<[]>([]);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [clickedOn, setClickedOn] = useState<string>('');
  const [openSendMsg, setOpenSendMsg] = useState<boolean>(false);
  const [applicationData, setApplicationData] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);

// function for handling document type options dynamically based on the checks
const handleDynamicOptions = (name: string, value: string) => {
  setDocumentOptions((prevOptions) => {
    let opt = [...prevOptions];

    // Check conditions for updating document options based on the name and value
    if (name === 'ProofOfNotProfitType') {
      if (value === 'NJ State Non-Profit ID') {
        opt = [...opt, 'NJ State Non-Profit ID (Required)'];
      } else {
        opt = [
          ...opt,
          'IRS Form 501c3 showing their Non-Profit status (Required)'
        ];
      }
    }

    if (name === 'WalmartUsed') {
      if (value === 'yes') {
        opt = [...opt, 'Wal-Mart Consent Form (Required)'];
      } else {
        opt = [
          ...opt,
          'Letter of Consent signed by the store owner or manager with the date(s) and time(s) listed (Required)'
        ];
      }
    }
    // Return the updated document options
    return opt;
  });
};

  // Function to handle form field changes
  const handleChange = (val, e) => {
    const data = applicationData;
    const { name } = e;
    if (name === 'WalmartUsed' || name === 'ProofOfNotProfitType') {
      data[name] = val;
      handleDynamicOptions(name, val);
      setApplicationData(data);
    } else {
      data[name] = val;
      setApplicationData(data);
    }
  };

  // Function to get application details
  const handleGetApplicationDetails = async () => {
    const isDraft = location?.state && (location?.state as DraftCheck)?.isDraft;
    const response = await getApplicationDetails({
      applicationID,
      permit,
      isDraft
    });
    if (response?.status === 'success') {
      setApplicationData(response?.data?.data[0]);
    }
  };

  // Use effect to get application details
  useEffect(() => {
    handleGetApplicationDetails();
  }, [applicationID, permit]);

  // Form fields for the application form using formik
  const formFields: Fields[] = [
    {
      fieldName: 'OrganizationName',
      fieldLabel: 'Organization Name',
      fieldType: 'text',
      required: true,
      initialValue: applicationData?.OrganizationName,
      dependency: handleChange
    },
    {
      fieldName: 'ContactPersonName',
      fieldLabel: "Contact Person's Name",
      fieldType: 'text',
      required: true,
      initialValue: applicationData?.ContactPersonName,
      dependency: handleChange
    },
    {
      fieldName: 'Address',
      fieldLabel: 'Address',
      fieldType: 'note',
      required: true,
      initialValue: applicationData?.Address,
      dependency: handleChange
    },
    {
      fieldName: 'Purpose',
      fieldLabel: 'Purpose',
      fieldType: 'note',
      required: true,
      initialValue: applicationData?.Purpose,
      dependency: handleChange
    },
    {
      fieldName: 'EmailAddress',
      fieldLabel: 'Email Address',
      fieldType: 'email',
      required: true,
      initialValue: applicationData?.EmailAddress,
      dependency: handleChange
    },
    {
      fieldName: 'PhoneNumber',
      fieldLabel: 'Phone Number',
      fieldType: 'mobile',
      required: true,
      initialValue: applicationData?.PhoneNumber,
      dependency: handleChange
    },
    {
      fieldName: 'NotProfitIdNumber',
      fieldLabel: 'Non-profit ID Number',
      fieldType: 'number',
      initialValue: applicationData?.NotProfitIdNumber,
      dependency: handleChange
    },
    {
      fieldName: 'ProofOfNotProfitType',
      fieldLabel: 'Which proof of non-profit do you have ?',
      fieldType: 'radio',
      options: [
        { label: 'NJ State Non-Profit ID', value: 'NJ State Non-Profit ID' },
        { label: 'IRS Form 501c3', value: 'IRS Form 501c3' }
      ],
      initialValue: applicationData?.ProofOfNotProfitType,
      dependency: handleChange,
      br: true
    },
    {
      fieldName: 'WalmartUsed',
      fieldLabel: 'This will be used at Wal-Mart',
      fieldType: 'radio',
      options: [
        { label: 'Yes', value: 'yes' },
        { label: 'No', value: 'no' }
      ],
      initialValue: applicationData?.WalmartUsed ? 'yes' : 'no',
      dependency: handleChange,
      br: true
    }
  ];

  const DynamicFormContent = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={6} mt={1}>
          <FormikSelect
            label={'Select document type to upload'}
            name={'documentType'}
            initialValue={applicationData?.documentType}
            options={documentOptions}
            fieldValue={{
              value: documentOptions
                ? applicationData?.documentType
                : 'Select Type'
            }}
            handleChange={(e) => {
              const { value } = e.target;
              setApplicationData({
                ...applicationData,
                documentType: [...uploadFiles, ' ' + value]?.toString()
              });
              const unique = [...uploadFiles, value]
                .filter((val, index, self) => self.indexOf(val) === index)
                .filter((type) => type !== 'Select Type');
              setUploadFiles(unique);
            }}
          />
          <>
            <Typography sx={{ color: '#8B0000' }} m={1}>
              {docsErrorMessege}
            </Typography>
            {uploadFiles?.length
              ? uploadFiles.map((item, i) => {
                  return (
                    <Box
                      key={i}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        background: 'lightgray',
                        width: '100%',
                        borderRadius: 1
                      }}
                      mt={1}
                    >
                      <Typography
                        display={'flex'}
                        alignItems={'center'}
                        px={'20px'}
                      >
                        <FilePresentIcon color="disabled" fontSize="small" />
                        {item}
                      </Typography>
                      <Box sx={{ display: 'flex' }}>
                        <Button
                          variant="outlined"
                          style={{ color: '#8B0000', border: 'none' }}
                          onClick={() => {
                            const updatedFiles = uploadFiles.filter(
                              (_, ind) => ind !== i
                            );
                            setUploadFiles(updatedFiles);
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </Button>
                      </Box>
                    </Box>
                  );
                })
              : null}
            <CustomButtonContained
              title={'Upload Documents'}
              sx={{ mt: 2 }}
              disabled={!uploadFiles?.length}
              onClick={() => {
                setIsOpen(true);
                setDocsErrorMessege('');
              }}
            />
          </>
          <br />
          {applicationData?.ApplicationStatus !== 'Draft' && (
            <Box mt={2}>
              <CustomButtonContained
                sx={{
                  mt: { xs: 1.5, sm: 0 },
                  width: { xs: '100%', sm: 'auto' }
                }}
                onClick={() => setSwitchTo('View Documents')}
                title={'View Documents'}
              />
              <CustomButtonContained
                sx={{
                  ml: { xs: '0', sm: 2 },
                  mt: { xs: 1.5, sm: 'auto' },
                  width: { xs: '100%', sm: 'auto' }
                }}
                title={'View Message History'}
                onClick={() => setSwitchTo('View Message History')}
              />
              {isManager && !shouldShowEdit(applicationData) && (
                <CustomButtonContained
                  sx={{
                    ml: { xs: '0', sm: 2 },
                    mt: { xs: 1.5, sm: 'auto' },
                    width: { xs: '100%', sm: 'auto' }
                  }}
                  onClick={() => setOpenSendMsg(true)}
                  title={'Send Message'}
                />
              )}
            </Box>
          )}
          {/* : null} */}
        </Grid>
      </Grid>
    );
  };

  const handleApproveApplication = async () => {
    setOpenApprovePopup(false)
    setLoading(true);
    const payload = {
      Purpose: applicationData?.Purpose,
      ApplicationID: Number(applicationID),
      ApplicationStatus: 'Approved',
      Address: applicationData?.Address,
      // ApplicationNumber:applicationData?.ApplicationNumber,
      OrganizationName: applicationData?.OrganizationName,
      ContactName: applicationData?.ContactPersonName,
      ContactPersonName: applicationData?.ContactPersonName,
      EmailAddress: applicationData?.EmailAddress,
      NotProfitIdNumber: applicationData?.NotProfitIdNumber,
      PhoneNumber: applicationData?.PhoneNumber,
      ProofOfNotProfitType: applicationData?.ProofOfNotProfitType,
      WalmartUsed: applicationData?.WalmartUsed
    };
    const response = await httpRequest(
      'PUT',
      `/applications/${permit}/update_status`,
      payload
    );
    if (response?.status === 'success') {
      setLoading(false)
      sendNotification(socket, {
        UserID: applicationData?.UserID, // receiver user ID
        ApplicationID: applicationID,
        PermitID: permit,
        TownshipID: townshipId,
        Title: 'Application Approved',
        Message: `Application no. ${applicationData?.ApplicationNumber} is approved.`,
        ApplicationNumber: applicationData?.ApplicationNumber
      });
      toast?.success(`Application Approved successfully.`);
      navigate(`/${townshipId}/Clerk Department/${permit}/application-list`);
    } else {
      toast?.error(response?.message);
      dispatch(handleError(response?.message));
      setLoading(false);
    }
  };
  const handleUpdate = async (data) => {
    setLoading(true);
    const requestData = { ...applicationData, ...data };
    const payload = {
      ApplicantName: `${userData.FirstName} ${userData.LastName}`,
      ApplicantPhoneNo: userDetails?.PhoneNumber,
      ApplicantAddress: userDetails?.TownshipName,
      Purpose: requestData?.Purpose,
      OrganizationName: requestData?.OrganizationName,
      ContactPersonName: requestData?.ContactPersonName,
      Address: requestData?.Address,
      EmailAddress: requestData?.EmailAddress,
      PhoneNumber: requestData?.PhoneNumber,
      NotProfitIdNumber: requestData?.NotProfitIdNumber,
      ProofOfNotProfitType: requestData?.ProofOfNotProfitType,
      WalmartUsed: requestData?.WalmartUsed === 'yes' ? true : false,
      DocumentFileUpload: JSON.stringify(file)
    };
    const response = await httpRequest(
      'PATCH',
      `/charity_solicitor_permit_application/update/${applicationID}`,
      payload
    );

    return response;
  };
  const handleSubmit = async () => {
    const payloadStatusChange = {
      ApplicationID: applicationData?.ApplicationID,
      ApplicationStatus: 'In Review',
      PermitID: Number(permit),
      PermitPrefix: 'CharSol'
    };

    const response = await httpRequest(
      'POST',
      '/payments/update_status',
      payloadStatusChange
    );

    if (response?.status === 'success') {
      toast.success('Application submitted succeessfully.');
      navigate(`/${townshipId}/Clerk Department/${permit}/application-list`);
    } else {
      toast.error(response?.message);
    }
  };
  const handleProceedToUpdateAndApprove = async (data) => {
    const response = await handleUpdate(data);
    if (clickedOn === 'Update') {
      if (response?.status === 'success') {
        toast.success('Application updated successfully.');
        setLoading(false);
        navigate(
          `/${townshipId}/Clerk Department/${permit}/${
            applicationData?.ApplicationStatus === 'Draft'
              ? 'draft-application'
              : 'application-list'
          }`
        );
      } else if (response?.type === 'NETWORK_ERROR') {
      } else {
        setLoading(false);
        toast.error(response?.message);
        dispatch(handleError(response?.message));
      }
    } else if (clickedOn === 'Approve') {
      if (response?.status === 'success') {
        setLoading(false)
        setOpenApprovePopup(true);
      }
    } else if (clickedOn === 'Submit') {
      if (response?.status === 'success') handleSubmit();
    }
  };

  const getFormButtons = () => {
    let buttonsCollection = ['Cancel'];
    if (applicationData?.ApplicationStatus) {
      if (applicationData?.ApplicationStatus === 'In Review') {
        buttonsCollection = [...buttonsCollection, 'Update'];
      }
      if (applicationData?.ApplicationStatus === 'Draft') {
        buttonsCollection = [...buttonsCollection, 'Update', 'Submit'];
      }
      if (isManager && applicationData?.ApplicationStatus === 'In Review') {
        buttonsCollection = [...buttonsCollection, 'Approve'];
      }
    }
    return buttonsCollection;
  };

  const handleMessageChange = (e) => {
    // setMessageBody(e.target.value)
    messageBodyRef.current = e.target.value;
  };

  return loading ? (
    <LoadingOverlay />
  ) : (
    <Container sx={{ mt: 2 }} className="main-table-container">
      <Breadcrumb
        PermitName={'Charitable Solicitations'}
        PageTitle={'Edit Application'}
      />
      <FormHeaderGreeting
        name={`${userData?.FirstName} ${userData?.LastName}`}
        applicationType="edit"
        applicationNumber={applicationData?.ApplicationNumber}
        applicationStatus={applicationData?.ApplicationStatus}
      />
      <Card>
        <>
          {switchTo === '' ? (
            <Box
              m={{ md: 3, xs: 1 }}
              sx={{
                display: 'flex',
                justifyContent: 'flex-end'
              }}
            >
              <PdfGenerator
                PermitName={'Charitable Solicitations'}
                allFields={formFields}
              />
            </Box>
          ) : null}
          <Box m={{ md: 3, xs: 1 }}>
            {switchTo !== '' ? (
              <EditFormfeatureViews
                switchTo={switchTo}
                setSwitchTo={setSwitchTo}
                applicationDetails={{
                  TownshipID: townshipId,
                  PermitName: 'CharSol',
                  applicationID: applicationID,
                  UserID: applicationData?.UserID,
                  ApplicationStatus: applicationData?.ApplicationStatus
                }}
              />
            ) : (
              <FormikForm
                allFields={formFields}
                buttonTitle={getFormButtons()}
                formMethod={handleProceedToUpdateAndApprove}
                buttonPlacement={'right'}
                setClickedOn={setClickedOn}
                clickProp
                content={<DynamicFormContent />}
                defaultEnabledButton={['Approve', 'Submit']}
                handleOtherButton={() => {
                  navigate(
                    `/${townshipId}/Clerk Department/${permit}/${
                      applicationData?.ApplicationStatus === 'Draft'
                        ? 'draft-application'
                        : 'application-list'
                    }`
                  );
                }}
              />
            )}
          </Box>
        </>
      </Card>
      <CustomModal
        title={'Upload documents'}
        IsOpen={IsOpen}
        setIsOpen={setIsOpen}
        loading={loading}
        onSave={() => setIsOpen(false)}
        actionButtons={['Save']}
        content={
          <UploadDocs
            documentToUpload={uploadFiles}
            ApplicationID={''}
            file={file}
            setFile={setFile}
            setLoading={setLoading}
            permitName={'CharSol'}
            reFetch={() => {}}
          />
        }
      />
      <CustomModal
        title={'Send Message'}
        IsOpen={openSendMsg}
        setIsOpen={setOpenSendMsg}
        messageBodyRef={messageBodyRef}
        actionButtons={['Cancel', 'Send']}
        loading={loading}
        setLoading={setLoading}
        applicationNumber={applicationData?.ApplicationNumber}
        ApplicationID={applicationID}
        applicationDetails={applicationData}
        content={
          <TextField
            className="txtArea"
            label="Message"
            placeholder="Message"
            name="message"
            multiline
            onChange={(e) => handleMessageChange(e)}
            sx={{ width: '600px' }}
            rows={3}
          />
        }
      />
      <CustomModal
        title={'Approve Application?'}
        IsOpen={openApprovePopup}
        setIsOpen={setOpenApprovePopup}
        onSave={handleApproveApplication}
        actionButtons={['No', 'Yes']}
        loading={loading}
        content={
          <Typography>
            Are you sure you want to approve this application?
          </Typography>
        }
      />
    </Container>
  );
};

export default EditApplication;
